import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  ActionRow,
  ModalDialog,
  Form,
  Alert,
  Badge,
  Spinner,
  Row,
  Col,
  Card,
} from '@openedx/paragon';
import { Check } from '@openedx/paragon/icons';
// eslint-disable-next-line import/no-unresolved
import { isOrgAdmin } from 'custom-slot-components';
import { getUserProfile, updateUserProfileComplete, updateEnterpriseLearner } from '../../../data/api';
import { fetchCompanyAdmins, refreshCompanyEmployees } from '../../../data/thunks';
import LearnerSearchDropdown, { LearnerOption } from './LearnerSearchDropdown';
import { logStudioHomeError } from '../../../logging';

interface UpdateLearnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  learners: LearnerOption[];
  selectedCompany: any | null;
  rosterIncomplete?: boolean;
}

interface FerpaVersion {
  version: string;
  acknowledged: boolean;
  acknowledged_timestamp: string;
  created: string;
}

interface EmployeeRecord {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  name: string;
  company_name: string;
  friendly_name: string;
  platform_org: string | null;
  is_admin: boolean;
  is_primary_admin: boolean;
  ferpa_accepted: boolean;
  accepted_date: string | null;
  ferpa: FerpaVersion | null;
  job_title: string | null;
  metadata: any;
}

interface UserProfile {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  is_alumni: boolean;
  person_id: string | null;
  postal_code: string | null;
  company: string | null;
  pd_terms_accepted: boolean;
  bongo_terms_accepted: boolean;
  must_update_pass: boolean;
  employeeRecords: EmployeeRecord[];
  auth_provider: string;
  is_pd_admin: boolean;
}

const UpdateLearnersModal: React.FC<UpdateLearnersModalProps> = ({
  isOpen,
  onClose,
  learners,
  selectedCompany,
  rosterIncomplete = false,
}) => {
  const dispatch = useDispatch();
  // Search state
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [learnerSearchValue, setLearnerSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [editedProfile, setEditedProfile] = useState<UserProfile | null>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState('');

  // Handle search for user
  const handleSearch = async (identifier?: string) => {
    const id = identifier !== undefined ? identifier : searchIdentifier;
    if (!id.trim()) {
      setSearchError('Please select a learner from the dropdown');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setUserProfile(null);
    setEditedProfile(null);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const profile = await getUserProfile(id.trim());

      // Validate user is in the selected company
      const employeeRecord = profile.employeeRecords?.find(
        (emp: any) => emp.company_name === selectedCompany?.company_name,
      );

      if (!employeeRecord) {
        setSearchError(`This user is not a member of ${selectedCompany?.friendly_name}`);
        return;
      }

      setUserProfile(profile);
      // Create a deep clone for editing
      setEditedProfile(JSON.parse(JSON.stringify(profile)));
    } catch (error: any) {
      logStudioHomeError('cmp_learner_profile_fetch_failed', error);
      if (error.response?.status === 404) {
        setSearchError('User not found. Please check the email address or username.');
      } else {
        setSearchError('Failed to fetch user profile. Please try again.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Handle learner selection from dropdown — pre-fill and auto-search
  const handleLearnerSelect = (learner: LearnerOption) => {
    setSearchIdentifier(learner.email);
    handleSearch(learner.email);
  };

  // Handle field changes
  const handleFieldChange = (field: keyof UserProfile, value: any) => {
    if (editedProfile) {
      setEditedProfile({
        ...editedProfile,
        [field]: value,
      });
    }
  };

  // Handle employee record field changes
  const handleEmployeeRecordFieldChange = (companyCode: string, field: string, value: any) => {
    if (editedProfile?.employeeRecords) {
      const updatedEmployeeRecords = [...editedProfile.employeeRecords];
      const index = updatedEmployeeRecords.findIndex(emp => emp.company_name === companyCode);
      if (index !== -1) {
        updatedEmployeeRecords[index] = {
          ...updatedEmployeeRecords[index],
          [field]: value,
        };
        setEditedProfile({
          ...editedProfile,
          employeeRecords: updatedEmployeeRecords,
        });
      }
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!editedProfile) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      // 1. Update general user profile
      await updateUserProfileComplete(editedProfile);

      // 2. Update specific employee record if it changed
      if (selectedCompany) {
        const employeeRecord = editedProfile.employeeRecords?.find(
          (emp: any) => emp.company_name === selectedCompany.company_name,
        );
        if (employeeRecord) {
          await updateEnterpriseLearner({
            company_name: selectedCompany.company_name,
            username: editedProfile.username,
            job_title: employeeRecord.job_title,
            // Keep other fields identical as they were or manage them accordingly
            is_admin: isOrgAdmin(editedProfile, selectedCompany.company_name),
          });
        }
      }

      setSubmissionSuccess('User profile updated successfully!');

      // Refresh the profile to show updated data
      const updatedProfile = await getUserProfile(editedProfile.email);
      setUserProfile(updatedProfile);
      setEditedProfile(JSON.parse(JSON.stringify(updatedProfile)));
      if (selectedCompany?.company_name) {
        await dispatch(refreshCompanyEmployees(selectedCompany.company_name) as any);
        dispatch(fetchCompanyAdmins() as any);
      }
    } catch (error: any) {
      logStudioHomeError('cmp_learner_profile_update_failed', error);
      setSubmissionError('Failed to update user profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle reset form
  const handleReset = () => {
    if (userProfile) {
      setEditedProfile(JSON.parse(JSON.stringify(userProfile)));
      setSubmissionError('');
      setSubmissionSuccess('');
    }
  };

  // Handle new search
  const handleNewSearch = () => {
    setSearchIdentifier('');
    setLearnerSearchValue('');
    setIsSearchFocused(false);
    setUserProfile(null);
    setEditedProfile(null);
    setSearchError('');
    setSubmissionError('');
    setSubmissionSuccess('');
  };

  // Handle modal close
  const handleClose = () => {
    handleNewSearch();
    onClose();
  };

  // Format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  // Check if form has changes
  const hasChanges = () => JSON.stringify(userProfile) !== JSON.stringify(editedProfile);

  return (
    <ModalDialog
      title="Update Learner Information"
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      hasCloseButton
      isFullscreenOnMobile
    >
      <ModalDialog.Header>
        <ModalDialog.Title>Update Learner Information</ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Search Form */}
        {!userProfile && (
          <div className="mb-4">
            <h5 className="mb-3">Search for Learner</h5>
            <Form.Group>
              <Form.Label>Learner</Form.Label>
              <div style={{ marginBottom: isSearchFocused ? '14rem' : undefined }}>
                <LearnerSearchDropdown
                  learners={learners}
                  value={learnerSearchValue}
                  onChange={setLearnerSearchValue}
                  onSelect={handleLearnerSelect}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setIsSearchFocused(false)}
                  placeholder="Search by name or email..."
                  disabled={isSearching}
                  id="update-learner-search"
                  companyName={selectedCompany?.company_name}
                  rosterIncomplete={rosterIncomplete}
                />
                {isSearching && (
                  <div className="mt-2 text-muted small d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading learner profile...
                  </div>
                )}
                <Form.Text className="text-muted">
                  Search for a learner by name or email to view and update their information.
                </Form.Text>
              </div>
            </Form.Group>

            {searchError && (
              <Alert variant="danger" className="mt-3">
                {searchError}
              </Alert>
            )}
          </div>
        )}

        {/* User Profile Edit Form */}
        {editedProfile && userProfile && (
          <div>
            {submissionError && (
              <Alert variant="danger" dismissible onClose={() => setSubmissionError('')}>
                {submissionError}
              </Alert>
            )}

            {submissionSuccess && (
              <Alert variant="success" dismissible onClose={() => setSubmissionSuccess('')}>
                {submissionSuccess}
              </Alert>
            )}

            {/* Button to search for another user */}
            <div className="mb-3">
              <Button variant="link" size="sm" onClick={handleNewSearch}>
                ← Search for another learner
              </Button>
            </div>

            {/* Basic Information Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Basic Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Username <small className="text-muted">(Read Only)</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={editedProfile.username}
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Email <small className="text-muted">(Read Only)</small>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        value={editedProfile.email}
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        First Name <small className="text-muted">(Read Only)</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={editedProfile.first_name}
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Last Name <small className="text-muted">(Read Only)</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={editedProfile.last_name}
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Full Name <small className="text-muted">(Read Only)</small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={editedProfile.name}
                        disabled
                        readOnly
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Postal Code</Form.Label>
                      <Form.Control
                        type="text"
                        value={editedProfile.postal_code || ''}
                        onChange={(e) => handleFieldChange('postal_code', e.target.value || null)}
                        placeholder="Enter postal code"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Terms & Settings Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Terms & Settings <small className="text-muted">(Read Only)</small></h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>PD Terms Accepted</Form.Label>
                      <div>
                        <Badge variant={editedProfile.pd_terms_accepted ? 'success' : 'secondary'}>
                          {editedProfile.pd_terms_accepted ? <><Check /> Accepted</> : 'Not Accepted'}
                        </Badge>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bongo Terms Accepted</Form.Label>
                      <div>
                        <Badge variant={editedProfile.bongo_terms_accepted ? 'success' : 'secondary'}>
                          {editedProfile.bongo_terms_accepted ? <><Check /> Accepted</> : 'Not Accepted'}
                        </Badge>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Must Update Password</Form.Label>
                      <div>
                        <Badge variant={editedProfile.must_update_pass ? 'warning' : 'success'}>
                          {editedProfile.must_update_pass ? 'Required' : 'Not Required'}
                        </Badge>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* B2B Organizations Section */}
            {editedProfile && selectedCompany && editedProfile.employeeRecords && (
            <div>
              <h5 className="mb-3">Organization Details</h5>
              {(() => {
                const employeeRecord = editedProfile.employeeRecords.find(
                  (emp: any) => emp.company_name === selectedCompany.company_name,
                );

                if (!employeeRecord) {
                  return (
                    <Alert variant="info">
                      This user is not associated with the selected organization.
                    </Alert>
                  );
                }

                return (
                  <Card className="mb-4">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">{employeeRecord.friendly_name}</h6>
                        <Badge variant="secondary">{employeeRecord.company_name}</Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Is Admin <small className="text-muted">(Read Only)</small>
                            </Form.Label>
                            <div>
                              <Badge variant={isOrgAdmin(editedProfile, selectedCompany?.company_name) ? 'primary' : 'secondary'}>
                                {isOrgAdmin(editedProfile, selectedCompany?.company_name) ? <><Check /> Admin</> : 'Not Admin'}
                              </Badge>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Job Title</Form.Label>
                            <Form.Control
                              type="text"
                              value={employeeRecord.job_title || ''}
                              onChange={(e) => handleEmployeeRecordFieldChange(selectedCompany?.company_name, 'job_title', e.target.value)}
                              placeholder="Enter job title"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              FERPA Acknowledged <small className="text-muted">(Read Only)</small>
                            </Form.Label>
                            <div>
                              <Badge variant={employeeRecord.ferpa?.acknowledged ? 'success' : 'secondary'}>
                                {employeeRecord.ferpa?.acknowledged ? <><Check /> Acknowledged</> : 'Not Acknowledged'}
                              </Badge>
                              {employeeRecord.ferpa?.acknowledged_timestamp && (
                              <span className="ms-2 text-muted small">
                                on {formatDate(employeeRecord.ferpa.acknowledged_timestamp)}
                              </span>
                              )}
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* FERPA Information */}
                      {employeeRecord.ferpa && (
                      <div className="mb-3">
                        <h6>FERPA Status <small className="text-muted">(Read Only)</small></h6>
                        <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                          <p className="mb-2">
                            <strong>Version:</strong> {employeeRecord.ferpa.version}
                          </p>
                          <p className="mb-2">
                            <strong>Created:</strong> {formatDate(employeeRecord.ferpa.created)}
                          </p>
                          <p className="mb-0">
                            <strong>Acknowledged:</strong> {employeeRecord.ferpa.acknowledged ? 'Yes' : 'No'}
                          </p>
                        </div>
                      </div>
                      )}
                    </Card.Body>
                  </Card>
                );
              })()}
            </div>
            )}

            {/* No Organization */}
            {!editedProfile?.employeeRecords?.find(
              (emp: any) => emp.company_name === selectedCompany?.company_name,
            ) && (
              <Alert variant="info">
                This user is not associated with the selected organization.
              </Alert>
            )}
          </div>
        )}
      </ModalDialog.Body>

      <ModalDialog.Footer>
        {!userProfile ? (
          <ActionRow>
            <ModalDialog.CloseButton variant="tertiary">
              Cancel
            </ModalDialog.CloseButton>
          </ActionRow>
        ) : (
          <ActionRow className="justify-content-between w-100">
            <ModalDialog.CloseButton variant="tertiary">
              Close
            </ModalDialog.CloseButton>
            <div className="d-flex">
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                disabled={isSubmitting || !hasChanges()}
                className="me-2"
              >
                Reset Changes
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !hasChanges()}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </ActionRow>
        )}
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

export default UpdateLearnersModal;
